import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * 🎛️ SystemOperationModeForm
 * ------------------------------------------------------------
 * Representa el formulario editable para la configuración
 * del modo operativo del sistema.
 */
export interface SystemOperationModeForm {
  id?: number | null;

  // 🎫 DTE
  dteEnabled: boolean;

  // 🖨️ Impresión
  printMode: 'LOCAL' | 'CLOUD';
  printEnabled: boolean;

  // 🏪 Operación de ventas
  saleOperationMode: 'DIRECT_SALE' | 'PENDING_CASHIER';

  // 📦 Inventario

  /**
   * Mostrar productos sin existencia.
   */
  showOutOfStockProducts: boolean;

  /**
   * Permitir ventas con inventario negativo.
   */
  allowNegativeStockSales: boolean;

  // ✅ Estado
  active: boolean;
}

/**
 * 🧠 Estado general del slice.
 */
interface SystemOperationModeState {
  form: SystemOperationModeForm;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
}

/**
 * 🔹 Estado inicial del slice.
 */
const initialState: SystemOperationModeState = {
  form: {
    id: null,
    dteEnabled: false,
    printMode: 'LOCAL',
    printEnabled: true, // ✅ Default recomendado
    saleOperationMode: 'DIRECT_SALE',
    showOutOfStockProducts: false,
    allowNegativeStockSales: false,
    active: true,
  },
  isLoading: false,
  isSubmitting: false,
  error: null,
  success: false,
};


/**
 * 🧩 Slice para manejar la configuración del modo de operación.
 */
const systemOperationModeSlice = createSlice({
  name: 'systemOperationMode',
  initialState,
  reducers: {
    // 🚀 Inicia carga de configuración
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
      state.success = false;
    },

    // ✅ Finaliza carga
    finishLoading(state) {
      state.isLoading = false;
    },

    // 💾 Cambia el estado de "submitting" manualmente
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },

    // ✅ Configuración cargada o guardada con éxito
    setConfiguration(state, action: PayloadAction<SystemOperationModeForm>) {
      state.form = action.payload;
      state.isLoading = false;
      state.isSubmitting = false;
      state.success = true;
      state.error = null;
    },

    // ❌ Error general
    setError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isSubmitting = false;
      state.success = false;
      state.error = action.payload;
    },

    // 🧩 Actualiza campos individuales del formulario
    updateFormField(
      state,
      action: PayloadAction<{ field: keyof SystemOperationModeForm; value: any }>
    ) {
      const { field, value } = action.payload;
      state.form = {
        ...state.form,
        [field]: value,
      };
    },

    // ♻️ Restablece el estado a inicial
    resetState(state) {
      state.form = initialState.form;
      state.isLoading = false;
      state.isSubmitting = false;
      state.error = null;
      state.success = false;
    },
  },
});

export const {
  startLoading,
  finishLoading,
  setSubmitting,
  setConfiguration,
  setError,
  updateFormField,
  resetState,
} = systemOperationModeSlice.actions;

export default systemOperationModeSlice.reducer;
